import React, { useEffect } from 'react';

import { useAtom, useSetAtom } from 'jotai';

import {
  uiRoutes,
  useAccountInfo,
  useLoanSummary,
  usePaymentMethods,
} from '@consumer-frontend/nc-api';
import {
  isLineOfCredit,
  LOC_SAME_DAY_DEBIT_CUTOFF,
  nowCentral,
  PAYMENT_METHODS,
  PaymentMethodT,
  today,
} from '@consumer-frontend/nc-utils';

import { NcAddDebitCard } from './NcAddDebitCard';
import { NcPaymentAccountLabel } from './NcPaymentAccountLabel';
import { NcPaymentMethodSameDayBanner } from './NcPaymentMethodSameDayBanner';
import {
  accountIdAtom,
  hasFetchErrorAtom,
} from '../../store';
import { NcLegacyLink } from '../NcLegacyLink';
import { NcRadioButton } from '../NcRadioButton';
import { NcRadioGroup } from '../NcRadioGroup';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/payment/method.scss';

export type NcPaymentMethodPropsT = {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: React.ReactNode;
  value?: PaymentMethodT;
};
export type NcPaymentMethodT = React.FC<NcPaymentMethodPropsT>;

export const NcPaymentMethod: NcPaymentMethodT = ({
  errorMessage,
  onChange,
  value,
  ...props
}) => {
  const [accountId] = useAtom(accountIdAtom);
  const {
    accountInfo: {
      debitCardLockActive,
      loanNumber,
      loanType,
    } = {},
  } = useAccountInfo(accountId);
  const setHasFetchError = useSetAtom(hasFetchErrorAtom);

  const { loanSummary: { nextPayment } = {} } = useLoanSummary(accountId, loanNumber);
  const { dueDate } = nextPayment || {};

  const {
    currentBankAccount,
    currentDebitCard,
    error,
  } = usePaymentMethods(accountId);

  const isNextDueDate = dueDate?.compare(today()) === 0;
  const isAfterLocDebitCutoff = isLineOfCredit(loanType) && nowCentral().hour >= LOC_SAME_DAY_DEBIT_CUTOFF;
  const showSameDayBanner = isNextDueDate && !isAfterLocDebitCutoff;

  let debitCardErrorMessage = errorMessage;

  if (currentDebitCard && !currentDebitCard.isValid) {
    debitCardErrorMessage = (
      <>Expired&nbsp;<span data-chromatic="ignore">{currentDebitCard.expirationDate}</span>. Update debit card.</>
    );
  }

  if (debitCardLockActive) {
    debitCardErrorMessage = undefined;
  }

  // Update global error state if there's an error fetching payment methods
  useEffect(
    () => {
      setHasFetchError((prev) => prev || Boolean(error));
    },
    [error, setHasFetchError],
  );

  return (
    <div className="nc-payment-method">
      <NcRadioGroup
        controlId="payment-method-selection"
        errorMessage={errorMessage as string}
        label="Payment Method"
        onChange={onChange}
        value={value}
        {...props}
      >
        {showSameDayBanner && <NcPaymentMethodSameDayBanner />}

        {currentBankAccount && <NcRadioButton
          checked={value === PAYMENT_METHODS.ACH}
          unavailable={!currentBankAccount.isValid}
          errorMessage={
            !currentBankAccount.isValid
              ? 'There is an issue with this account. Update bank account.'
              : errorMessage
          }
          groupError={Boolean(errorMessage)}
          id="payment-method-selection-ach"
          label={
            <NcPaymentAccountLabel
              accountLast4={currentBankAccount.last4AccountNumber}
              paymentType={PAYMENT_METHODS.ACH}
            />
          }
          onChange={onChange}
          value={PAYMENT_METHODS.ACH}
        />}

        {currentDebitCard && <NcRadioButton
          checked={value === PAYMENT_METHODS.DEBIT_CARD}
          unavailable={!currentDebitCard.isValid || debitCardLockActive}
          errorMessage={debitCardErrorMessage}
          groupError={Boolean(errorMessage)}
          id="payment-method-selection-debit"
          label={<NcPaymentAccountLabel
            accountLast4={currentDebitCard.last4}
            paymentType={PAYMENT_METHODS.DEBIT_CARD}
          />}
          onChange={onChange}
          value={PAYMENT_METHODS.DEBIT_CARD}
        />}

        {!currentDebitCard && !debitCardLockActive && <NcAddDebitCard />}
      </NcRadioGroup>

      <NcLegacyLink
        className="manage-payment-methods"
        href={uiRoutes.legacyAH.managePaymentMethods}
      >
        Manage Payment Methods
      </NcLegacyLink>
    </div>
  );
};
